import { useEffect, useId, useImperativeHandle, useRef, useState, type Ref } from "react";

import { ProblemError, setGratingType, type GratingTypeOut, type GratingTypeWord } from "@/api";
import { asksBeforeTypeChange, GratingTypeQuestion } from "@/components/GratingTypeQuestion";
import { AlertIcon } from "@/components/icons";
import { Select } from "@/components/fields/Select";
import {
  gratingTypeOptions,
  gratingTypeRefusal,
  isGratingTypeWord,
  type CatalogueSpec,
  type LaidGratings,
} from "@/lib/gratingTypes";

import styles from "./JobGratingType.module.css";

/**
 * What the head may ask of the select from outside it: a type chosen the way
 * the select chooses — „Barfußrost übernehmen“ beside what the mails name
 * (Task 15a), the same question and the same write.
 */
export interface JobGratingTypeHandle {
  choose: (value: GratingTypeWord) => void;
}

interface JobGratingTypeProps {
  /** The head's way to choose from elsewhere (`JobGratingTypeHandle`). */
  handle?: Ref<JobGratingTypeHandle>;
  /** The select's id — the head's „Rosttyp“ label points at it. */
  id: string;
  caseId: string;
  /** The type the Vorgang has, as the plan answer states it. */
  type: string;
  /**
   * A change that went through and waits for the plan that carries it — its
   * type, or `null` (Ruling G-3). Until the board has hydrated from that
   * plan the select shows it and takes no other choice: the board's own
   * writes are held meanwhile, and a second change would go out on ground
   * about to be replaced.
   */
  arriving: string | null;
  /** The plan answer's catalogue — the widths a select names. */
  specs: readonly CatalogueSpec[];
  /** A released Vorgang (`quote_approved`): the release goes with the change, and the question says so (R-4). */
  approved: boolean;
  /** The release that would go — `null` while it is not loaded; the question is asked all the same. */
  approval: { by: string | null; at: string } | null;
  /** `null` where nothing lies: the change goes straight through. */
  laid: LaidGratings | null;
  /**
   * The board's own writes go first (`settleWrites`): a gesture still in its
   * settle window would otherwise land on the Stand the change wrote, and
   * come back 409. `null` where a reload is on its way — nothing is changed
   * on ground about to be replaced.
   */
  settle: () => Promise<number | null>;
  /** The change is on the wire (`true`), and has come back (`false`). */
  onWriting: (writing: boolean) => void;
  /** The Vorgang has the new type, and the Stand the answer names: the board reads its plan again. */
  onChanged: (saved: GratingTypeOut, next: GratingTypeWord) => void;
  /**
   * Refused with a 409 — the Vorgang was cancelled or its offer went out
   * under the board: the head is about to give way to the reading, so the
   * sentence is the board's notice to say, not this select's.
   */
  onConflict: (sentence: string) => void;
}

/**
 * „Rosttyp“ in the board's head (spec 2026-10-23 R-7) — the Vorgang's type,
 * changeable where it used to be a reading.
 *
 * **Not optimistic**, like a measure (`WallMeasureField`): a type moves the
 * width of every grating that follows it, and the server writes a Stand for
 * it (R-1). While the change is out, and until the plan that carries it has
 * put the board back (`arriving`, Ruling G-3), the select shows the type on
 * its way, is busy and takes no other choice; a refusal puts the type the
 * Vorgang has back.
 *
 * With nothing laid it goes straight through. With gratings laid it asks
 * first what happens to them — „Die Roste werden 288 statt 300 mm breit; die
 * Kalkulation wird neu.“, and what a person set by hand stays (Ruling G-1) —
 * and on a released Vorgang the release sentence every facade gesture asks
 * with stands above that (R-4, one dialog, as „Form ändern …“ has it). The
 * question is the Eingang's too (`GratingTypeQuestion`, Ruling G-2).
 *
 * Only for a Vorgang whose facade may change: the head shows a reading from
 * „Angebot raus“ on, on a cancelled Vorgang and on the register demo's board.
 */
export function JobGratingType({
  handle,
  id: selectId,
  caseId,
  type,
  arriving,
  specs,
  approved,
  approval,
  laid,
  settle,
  onWriting,
  onChanged,
  onConflict,
}: JobGratingTypeProps) {
  const refusalId = `${useId()}-refused`;
  const [asking, setAsking] = useState<GratingTypeWord | null>(null);
  /** The type on the wire — from the press until the answer is back. */
  const [pending, setPending] = useState<GratingTypeWord | null>(null);
  const [refused, setRefused] = useState<string | null>(null);

  const locked = pending !== null || arriving !== null;
  const shown = pending ?? arriving ?? type;

  async function change(next: GratingTypeWord): Promise<void> {
    setPending(next);
    setRefused(null);
    let posted = false;
    try {
      // Inside the `try`: a board write that fails while this waits must not
      // leave the select locked for good.
      const expected = await settle();
      if (expected === null) return;
      posted = true;
      onWriting(true);
      const saved = await setGratingType(caseId, next);
      onChanged(saved, next);
    } catch (cause: unknown) {
      const sentence = gratingTypeRefusal(cause);
      // Cancelled or sent under the board (409): the board says it above
      // itself and reads the Vorgang again, and the head gives way to the
      // reading. Anything else — a Stand without a measure (422), the
      // network — leaves the select where it is, and it says why beneath.
      if (cause instanceof ProblemError && cause.status === 409) onConflict(sentence);
      else setRefused(sentence);
    } finally {
      setPending(null);
      if (posted) onWriting(false);
    }
  }

  function choose(value: string): void {
    if (locked || !isGratingTypeWord(value) || value === type) return;
    setRefused(null);
    if (asksBeforeTypeChange(approved, laid)) {
      setAsking(value);
      return;
    }
    void change(value);
  }
  // Always the newest `choose`: the handle outlives the render it was made in.
  const chooseRef = useRef(choose);
  useEffect(() => {
    chooseRef.current = choose;
  });
  useImperativeHandle(handle, () => ({ choose: (value) => chooseRef.current(value) }), []);

  return (
    <>
      <span className={styles.value}>
        {/* Not `disabled` while the change is out: a select that is disabled
            under the focus drops it to the page. `aria-disabled` says it is
            locked; it is controlled — the value is the type on its way until
            the plan has hydrated — and a choice made meanwhile is not taken
            (`choose`). */}
        <Select
          id={selectId}
          className={styles.select}
          value={shown}
          aria-busy={locked ? true : undefined}
          aria-disabled={locked ? true : undefined}
          aria-describedby={refused !== null ? refusalId : undefined}
          onChange={(event) => choose(event.target.value)}
        >
          {/* A stored word the catalogue does not know stays itself (SelectField's rule). */}
          {isGratingTypeWord(shown) ? null : <option value={shown}>{shown}</option>}
          {gratingTypeOptions(specs).map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
        {refused !== null ? (
          <span id={refusalId} role="alert" className={styles.refused}>
            <AlertIcon size={14} className={styles.refusedMark} />
            <span>{refused}</span>
          </span>
        ) : null}
      </span>
      {asking !== null ? (
        <GratingTypeQuestion
          from={type}
          to={asking}
          specs={specs}
          approved={approved}
          approval={approval}
          laid={laid}
          onCancel={() => setAsking(null)}
          onConfirm={() => {
            const next = asking;
            setAsking(null);
            void change(next);
          }}
        />
      ) : null}
    </>
  );
}
